// User management. Admin only (spec: 01-users-permissions.md).
//
// Prices sit next to the role picker on purpose: inviting someone is the moment
// a customer's bill changes, and finding that out later is the kind of surprise
// this product is meant to be the opposite of.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, type User, type UserRole } from '../api';
import { scheduling, type Resource } from '../schedulingApi';
import { billing, money, type PriceCard } from '../billingApi';
import { useSession } from '../session';
import { Banner, Field } from '../ui';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CardList, RowCard } from '../components/ui/RowCard';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { Modal } from '../components/ui/Modal';

type SortKey = 'name' | 'email' | 'role' | 'status' | 'linked';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  field: 'Field worker',
};

/**
 * Prices come from the server, not from here.
 *
 * Spec 09: "Prices live in one place in tenant-facing UI (invite screen,
 * billing page) so the provisional card can change before go-live without a
 * spec edit." Phase 6a made `GET /api/billing/prices` that one place; a literal
 * in this file would be a second one, and the card is still marked provisional
 * in docs/pricing-analysis.md.
 *
 * The fallback below is only for the moment before the fetch lands, and is
 * deliberately vague rather than a number that could be wrong.
 */
const PRICE_PENDING = '…';

export function UsersPage() {
  const { user, refresh: refreshSession } = useSession();
  const [users, setUsers] = useState<User[] | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resettingTotp, setResettingTotp] = useState<User | null>(null);
  const [renaming, setRenaming] = useState<User | null>(null);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('name');

  const linkedName = useCallback(
    (userId: string) => resources.find((r) => r.linkedUserId === userId)?.name ?? null,
    [resources]
  );

  const visible = useMemo(
    () =>
      sortRows(
        users ?? [],
        (person) => {
          switch (sortKey) {
            case 'name':
              return person.name;
            case 'email':
              return person.email;
            case 'role':
              return ROLE_LABEL[person.role];
            case 'status':
              return person.status;
            case 'linked':
              return linkedName(person.id);
          }
        },
        sortDir
      ),
    [users, sortKey, sortDir, linkedName]
  );

  const load = useCallback(async () => {
    try {
      const { users: list } = await api.listUsers();
      setUsers(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your people.');
    }
  }, []);

  useEffect(() => {
    void load();
    void scheduling
      .listResources()
      .then((r) => setResources(r.resources))
      .catch(() => setResources([]));
  }, [load]);

  async function change(id: string, body: { role?: UserRole; status?: 'active' | 'deactivated' }) {
    try {
      await api.updateUser(id, body);
      setNotice('Updated.');
      setError(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make that change.');
      setNotice(null);
    }
  }

  if (!user) return null;

  return (
    <>
      {/* "Users", not "People" (round 30): People already means the
          person-kind resources, and one word carrying both ideas was a real
          ambiguity — this page is accounts and seats, not crew records. */}
      <h1>Users</h1>
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      {resettingTotp && (
        <ResetTotpDialog
          person={resettingTotp}
          onClose={() => setResettingTotp(null)}
          onDone={(message) => {
            setResettingTotp(null);
            setNotice(message);
            setError(null);
            void load();
          }}
        />
      )}

      {renaming && (
        <RenameDialog
          person={renaming}
          onClose={() => setRenaming(null)}
          onDone={(message) => {
            setRenaming(null);
            setNotice(message);
            setError(null);
            void load();
            // Renaming yourself through this page should not leave the
            // sidebar footer showing the old name until the next full load.
            if (renaming.id === user?.id) void refreshSession();
          }}
        />
      )}

      <InviteForm
        onInvited={(message) => {
          setNotice(message);
          setError(null);
          void load();
        }}
        onError={(message) => {
          setError(message);
          setNotice(null);
        }}
      />

      {users === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
        <CardList>
          {visible.map((person) => {
            const linked = linkedName(person.id);
            return (
              <RowCard
                key={person.id}
                title={person.name}
                badges={<StatusBadge status={person.status} kind="user" />}
                actions={
                  <>
                    <select
                      className="form-select w-auto"
                      aria-label={`Role for ${person.name}`}
                      value={person.role}
                      onChange={(event) =>
                        void change(person.id, { role: event.target.value as UserRole })
                      }
                    >
                      <option value="field">{ROLE_LABEL.field}</option>
                      <option value="manager">{ROLE_LABEL.manager}</option>
                      <option value="admin">{ROLE_LABEL.admin}</option>
                    </select>
                    <button
                      type="button"
                      className="row-action-muted"
                      onClick={() => setRenaming(person)}
                    >
                      Rename
                    </button>
                    {person.id !== user.id && (
                      <button
                        type="button"
                        className="row-action-muted"
                        onClick={() => setResettingTotp(person)}
                      >
                        Reset 2FA
                      </button>
                    )}
                    {person.id !== user.id && (
                      <button
                        type="button"
                        className={
                          person.status === 'deactivated' ? 'row-action-success' : 'row-action-muted'
                        }
                        onClick={() =>
                          void change(person.id, {
                            status: person.status === 'deactivated' ? 'active' : 'deactivated',
                          })
                        }
                      >
                        {person.status === 'deactivated' ? 'Switch back on' : 'Switch off'}
                      </button>
                    )}
                  </>
                }
              >
                <div className="break-words">{person.email}</div>
                {linked && <div>Linked to {linked}</div>}
              </RowCard>
            );
          })}
        </CardList>
        <div className="table-wrap max-sm:hidden">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader label="Name" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Email" sortKey="email" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Role" sortKey="role" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Linked resource" sortKey="linked" current={sortKey} dir={sortDir} onSort={onSort} />
                <th className="w-px" />
              </tr>
            </thead>
            <tbody>
              {visible.map((person) => (
                <tr key={person.id}>
                  <td className="font-medium">{person.name}</td>
                  <td className="table-muted">{person.email}</td>
                  <td>
                    <select
                      className="form-select w-40 py-1.5"
                      aria-label={`Role for ${person.name}`}
                      value={person.role}
                      onChange={(event) =>
                        void change(person.id, { role: event.target.value as UserRole })
                      }
                    >
                      <option value="field">{ROLE_LABEL.field}</option>
                      <option value="manager">{ROLE_LABEL.manager}</option>
                      <option value="admin">{ROLE_LABEL.admin}</option>
                    </select>
                  </td>
                  <td>
                    <StatusBadge status={person.status} kind="user" />
                  </td>
                  <td className="table-muted">
                    {resources.find((r) => r.linkedUserId === person.id)?.name ?? '—'}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="row-action-muted"
                      onClick={() => setRenaming(person)}
                    >
                      Rename
                    </button>
                    {person.id !== user.id && (
                      <>
                        <button
                          type="button"
                          className="row-action-muted"
                          onClick={() => setResettingTotp(person)}
                        >
                          Reset 2FA
                        </button>
                        <button
                          type="button"
                          className={
                            person.status === 'deactivated'
                              ? 'row-action-success'
                              : 'row-action-muted'
                          }
                          onClick={() =>
                            void change(person.id, {
                              status: person.status === 'deactivated' ? 'active' : 'deactivated',
                            })
                          }
                        >
                          {person.status === 'deactivated' ? 'Switch back on' : 'Switch off'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <p className="hint mt-3">
        Link someone to a resource on the <strong>Resources</strong> tab; that is what gives them
        their own schedule in the app.
      </p>
    </>
  );
}

function InviteForm({
  onInvited,
  onError,
}: {
  onInvited: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('field');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [prices, setPrices] = useState<PriceCard | null>(null);

  useEffect(() => {
    // If this fails the form still works; it just shows no price, which is
    // better than showing a stale one.
    void billing
      .prices()
      .then(setPrices)
      .catch(() => setPrices(null));
  }, []);

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFields({});
    try {
      await api.inviteUser({ name, email, role });
      setName('');
      setEmail('');
      setRole('field');
      onInvited(`Invitation sent to ${email}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        onError(err.message);
        setFields(err.fields);
      } else {
        onError('Could not send that invitation.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Invite someone</h2>
      <form onSubmit={invite} noValidate>
        <div className="row">
          <Field label="Name" name="inviteName" value={name} onChange={setName} error={fields.name} />
          <Field
            label="Email"
            name="inviteEmail"
            type="email"
            value={email}
            onChange={setEmail}
            error={fields.email}
          />
          <label htmlFor="inviteRole" style={{ flex: '0 1 14rem' }}>
            Role
            <select
              id="inviteRole"
              value={role}
              onChange={(event) => setRole(event.target.value as UserRole)}
            >
              {(['field', 'manager', 'admin'] as UserRole[]).map((option) => {
                const seat = prices?.seats.find((entry) => entry.role === option);
                return (
                  <option key={option} value={option}>
                    {ROLE_LABEL[option]},{' '}
                    {seat ? money(seat.priceCents) : PRICE_PENDING}/month
                  </option>
                );
              })}
            </select>
          </label>
          <button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          Per person per month, including GST, billed for the people who were active that month. Switch
          someone off and they cost nothing from the next month.
        </p>
      </form>
    </section>
  );
}

/**
 * Fixing somebody's name — most often a typo made on the invite form, which
 * until 2026-09-14 could not be corrected from any screen: the server's PATCH
 * had accepted `name` all along and nothing ever sent it (round 33's
 * unused-PATCH shape again). A name-only change touches nothing else — no
 * role, no status, no seat ledger.
 */
function RenameDialog({
  person,
  onClose,
  onDone,
}: {
  person: User;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [name, setName] = useState(person.name);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.updateUser(person.id, { name });
      onDone(`${person.name} is now ${result.user.name}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not rename them.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Rename ${person.name}`} onClose={onClose}>
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field
          label="Name"
          name="rename-name"
          value={name}
          onChange={setName}
          error={fields.name}
          autoComplete="off"
          enterKeyHint="go"
        />
        <p className="hint mb-4">
          Their sign-in email stays the same; they can change that themselves under
          Settings → Security. Notes and log entries they have already written keep the
          name they were written under.
        </p>
        <div className="row">
          <button type="submit" disabled={busy || name.trim() === person.name}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * One admin clearing a colleague's second factor.
 *
 * THE CREDENTIALS ASKED FOR ARE THE ACTOR'S, NOT THE COLLEAGUE'S, and the
 * dialog says so out loud — because the obvious reading of "reset their
 * two-factor" is that it will want something of theirs, and a person hunting
 * for a code that belongs to somebody who has lost their phone would give up.
 *
 * It is also what secures the endpoint. The person being reset can prove
 * nothing; that is why they need resetting. So what is checked is that the
 * admin doing it is really at the keyboard, and not a stolen session quietly
 * stripping the protection off every other admin in the company.
 */
function ResetTotpDialog({
  person,
  onClose,
  onDone,
}: {
  person: User;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.resetUserTotp(person.id, {
        currentPassword: password,
        ...(code.trim() === '' ? {} : { totpCode: code }),
      });
      onDone(
        result.reset
          ? `${person.name} can now sign in without a code, and has been emailed. They will be asked to set it up again.`
          : `${person.name} did not have two-factor authentication turned on.`
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not reset that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Reset two-factor for ${person.name}`} onClose={onClose}>
      <p className="mb-4 text-sm text-gray-600">
        Use this when someone has lost the phone their authenticator app was on and cannot
        use a recovery code either. It removes their codes, signs them out everywhere, and
        emails them. They set it up again next time they sign in.
      </p>
      {error && <Banner kind="error">{error}</Banner>}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="mb-2 text-sm font-medium text-gray-900">
          Confirm it is you: YOUR password and YOUR code, not theirs.
        </p>
        <Field
          label="Your password"
          name="reset-password"
          type="password"
          value={password}
          onChange={setPassword}
          error={fields.currentPassword}
          autoComplete="current-password"
        />
        <Field
          label="Your authenticator code"
          name="reset-code"
          value={code}
          onChange={setCode}
          error={fields.totpCode}
          autoComplete="one-time-code"
          inputMode="numeric"
          autoCapitalize="none"
          spellCheck={false}
          required={false}
          hint="Leave blank if you have not set up two-factor on your own account."
          enterKeyHint="go"
        />
        <div className="row">
          <button type="submit" disabled={busy}>
            {busy ? 'Resetting…' : 'Reset their two-factor'}
          </button>
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
